import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { crmJobsAPI } from '../lib/api';
import { predictiveRestockAPI } from '../lib/v2Api';
import { AppDrawer } from './ui/AppDrawer';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { Loader2, ShieldCheck, CircleAlert, AlertTriangle, Truck, RefreshCw, Package, Clock, Building2, User, Save, X, Wrench, Check } from 'lucide-react';
import { toast } from 'sonner';

export function PrepareTruckDrawer({ open, onClose, jobId, onRestocked }) {
  const navigate = useNavigate();
  const [readiness, setReadiness] = useState(null);
  const [jobData, setJobData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [restockMode, setRestockMode] = useState(false);
  const [restockRows, setRestockRows] = useState([]);
  const [restockSaving, setRestockSaving] = useState(false);

  const fetchData = useCallback(async () => {
    if (!jobId || !open) return;
    setLoading(true);
    setRestockMode(false);
    try {
      const [jobRes, readinessRes] = await Promise.allSettled([
        crmJobsAPI.get(jobId),
        crmJobsAPI.readiness(jobId),
      ]);
      if (jobRes.status === 'fulfilled') setJobData(jobRes.value.data);
      if (readinessRes.status === 'fulfilled') setReadiness(readinessRes.value.data);
      else setReadiness(null);
    } catch {
      toast.error('Failed to load truck preparation data');
    } finally {
      setLoading(false);
    }
  }, [jobId, open]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const missingItems = readiness?.items?.filter(i => i.status === 'missing' || i.status === 'partial') || [];
  const hasMissing = missingItems.length > 0;
  const isReady = readiness?.status === 'ready';
  const noKit = !readiness || readiness.status === 'no_kit';

  const openRestockReview = () => {
    setRestockRows(missingItems.map(i => ({
      item_id: i.item_id,
      item_name: i.item_name,
      sku: i.sku,
      quantity_required: i.quantity_required,
      quantity_on_van: i.quantity_on_van,
      needed: Math.max(0, i.quantity_required - i.quantity_available),
      editQty: Math.max(0, i.quantity_required - i.quantity_available),
    })));
    setRestockMode(true);
  };

  const updateRestockQty = (itemId, value) => {
    setRestockRows(prev => prev.map(r =>
      r.item_id === itemId ? { ...r, editQty: Math.max(0, parseInt(value) || 0) } : r
    ));
  };

  const removeRestockRow = (itemId) => {
    setRestockRows(prev => prev.filter(r => r.item_id !== itemId));
  };

  const handleSaveRestock = async () => {
    const valid = restockRows.filter(r => r.editQty > 0);
    if (!valid.length) { toast.error('No items to restock'); return; }
    setRestockSaving(true);
    try {
      const res = await predictiveRestockAPI.savePlan({
        name: `Truck Prep: ${jobData?.job_number || 'Job'} — ${readiness?.vehicle_name || 'Vehicle'}`,
        items: valid.map(r => ({
          item_id: r.item_id,
          source_type: 'vehicle',
          source_id: readiness.vehicle_id,
          suggested_qty: r.editQty,
        })),
      });
      toast.success(`Restock list created with ${res.data.total_items} items`);
      setRestockMode(false);
      onClose?.();
      onRestocked?.();
      navigate('/replenishment');
    } catch {
      toast.error('Failed to save restock plan');
    } finally {
      setRestockSaving(false);
    }
  };

  const statusIcon = (status) => {
    switch (status) {
      case 'ok': return <Check className="w-3.5 h-3.5 text-green-600" />;
      case 'missing': return <CircleAlert className="w-3.5 h-3.5 text-red-600" />;
      case 'partial': return <AlertTriangle className="w-3.5 h-3.5 text-orange-600" />;
      case 'low_stock': return <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />;
      default: return null;
    }
  };

  const statusLabel = (status) => {
    switch (status) {
      case 'ok': return 'Ready';
      case 'missing': return 'Missing';
      case 'partial': return 'Low Stock';
      case 'low_stock': return 'Low Stock';
      default: return status;
    }
  };

  return (
    <AppDrawer
      open={open}
      onClose={onClose}
      title={restockMode ? 'Review Restock Plan' : 'Prepare Truck'}
      description={restockMode
        ? `${readiness?.vehicle_name || 'Vehicle'} — ${jobData?.job_number || ''}`
        : 'Prepare the assigned vehicle with all parts required for this job.'
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : restockMode ? (
        /* ======= RESTOCK REVIEW MODE ======= */
        <div className="space-y-4" data-testid="prepare-truck-restock-review">
          <p className="text-sm text-muted-foreground">
            Review and adjust quantities. This creates a replenishment list for warehouse-to-vehicle transfer.
          </p>

          {restockRows.length > 0 ? (
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-right w-20">Need</TableHead>
                    <TableHead className="text-right w-28">Restock Qty</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {restockRows.map((row) => (
                    <TableRow key={row.item_id} data-testid={`prep-restock-row-${row.item_id}`}>
                      <TableCell>
                        <div className="text-sm font-medium">{row.item_name}</div>
                        {row.sku && <div className="text-xs text-muted-foreground">{row.sku}</div>}
                      </TableCell>
                      <TableCell className="text-right text-sm">{row.needed}</TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          min={0}
                          value={row.editQty}
                          onChange={(e) => updateRestockQty(row.item_id, e.target.value)}
                          className="w-20 h-8 text-right text-sm ml-auto"
                          data-testid={`prep-restock-qty-${row.item_id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => removeRestockRow(row.item_id)}>
                          <X className="w-3 h-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">No items in the restock plan.</p>
          )}

          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setRestockMode(false)}>
              Back
            </Button>
            <Button
              className="flex-1"
              onClick={handleSaveRestock}
              disabled={restockSaving || !restockRows.some(r => r.editQty > 0)}
              data-testid="prep-save-restock-btn"
            >
              {restockSaving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
              Save Restock List
            </Button>
          </div>
        </div>
      ) : (
        /* ======= MAIN PREPARE TRUCK VIEW ======= */
        <div className="space-y-5" data-testid="prepare-truck-drawer">
          {/* Job Summary */}
          <div className="space-y-2" data-testid="prepare-truck-summary">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Job Summary</h4>
            <div className="rounded-md border p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Package className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="text-sm font-medium" data-testid="prep-job-name">{jobData?.job_name || jobData?.job_number || 'Untitled Job'}</span>
                {jobData?.job_number && jobData?.job_name && (
                  <span className="text-xs text-muted-foreground">({jobData.job_number})</span>
                )}
              </div>
              {jobData?.customer_name && (
                <div className="flex items-center gap-2">
                  <Building2 className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <span className="text-sm" data-testid="prep-customer">{jobData.customer_name}</span>
                </div>
              )}
              {readiness?.assigned_user_name && (
                <div className="flex items-center gap-2">
                  <User className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <span className="text-sm" data-testid="prep-tech">{readiness.assigned_user_name}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Truck className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="text-sm" data-testid="prep-vehicle">
                  {readiness?.vehicle_name || <span className="text-muted-foreground italic">No vehicle assigned</span>}
                </span>
              </div>
            </div>
          </div>

          {/* No Kit case */}
          {noKit && (
            <div className="rounded-md border border-dashed p-6 text-center">
              <Package className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No Job Kit assigned to this job. Add a template with parts to enable truck preparation.</p>
            </div>
          )}

          {/* Ready indicator (17D) */}
          {isReady && (
            <div className="rounded-md border border-green-200 bg-green-50 dark:border-green-900/30 dark:bg-green-950/20 p-4 flex items-center gap-3" data-testid="truck-ready-indicator">
              <ShieldCheck className="w-6 h-6 text-green-600 dark:text-green-400 shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-800 dark:text-green-300">Vehicle is ready for this job.</p>
                <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                  All {readiness?.total_items} required part{readiness?.total_items !== 1 ? 's are' : ' is'} available on {readiness?.vehicle_name || 'the vehicle'}.
                </p>
              </div>
            </div>
          )}

          {/* Required Parts table (17B) */}
          {!noKit && readiness?.items?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Required Parts</h4>
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right w-20">Required</TableHead>
                      <TableHead className="text-right w-20">On Van</TableHead>
                      <TableHead className="text-right w-20">Missing</TableHead>
                      <TableHead className="w-24">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {readiness.items.map((item) => {
                      const missing = Math.max(0, item.quantity_required - item.quantity_available);
                      return (
                        <TableRow key={item.item_id} data-testid={`prep-part-row-${item.item_id}`}>
                          <TableCell>
                            <div className="text-sm font-medium">{item.item_name}</div>
                            {item.sku && <div className="text-xs text-muted-foreground">{item.sku}</div>}
                          </TableCell>
                          <TableCell className="text-right text-sm">{item.quantity_required}</TableCell>
                          <TableCell className="text-right text-sm">{item.quantity_on_van}</TableCell>
                          <TableCell className="text-right text-sm font-medium">
                            {missing > 0 ? <span className="text-red-600">{missing}</span> : <span className="text-muted-foreground">0</span>}
                          </TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                              item.status === 'ok' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                              item.status === 'missing' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                              item.status === 'partial' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' :
                              'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                            }`}>
                              {statusIcon(item.status)} {statusLabel(item.status)}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Summary bar */}
              <div className="flex items-center gap-4 text-xs text-muted-foreground px-1">
                <span>{readiness.ready_count} ready</span>
                {readiness.missing_count > 0 && <span className="text-red-600 font-medium">{readiness.missing_count} missing</span>}
                {readiness.low_stock_count > 0 && <span className="text-amber-600 font-medium">{readiness.low_stock_count} low stock</span>}
              </div>
            </div>
          )}

          {/* Restock action (17C) */}
          {hasMissing && readiness?.vehicle_id && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    className="w-full"
                    onClick={openRestockReview}
                    data-testid="prep-create-restock-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" /> Create Restock Plan
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Generate a replenishment list from warehouse to the assigned vehicle for missing parts.</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {/* No vehicle warning */}
          {readiness?.status === 'no_vehicle' && (
            <div className="rounded-md border border-amber-200 bg-amber-50 dark:border-amber-900/30 dark:bg-amber-950/20 p-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
              <p className="text-xs text-amber-700 dark:text-amber-400">
                No vehicle is assigned. Assign a technician with a vehicle via the Dispatch Board to enable truck preparation.
              </p>
            </div>
          )}
        </div>
      )}
    </AppDrawer>
  );
}
